import i18n from '@/i18n';
import { CHARACTER_MATCH_OPTIONS, CHARACTER_MATCH_ARRAY_OPTIONS, VERSION_MATCH_OPTIONS } from '@/presentation/components/form/standards/types';
import { iconText } from '@/presentation/components/hint-text/hintText';
import { enumFilterTips, versionFilterTips, stringFilterTips } from '@/presentation/components/issue-list/components/tips';
import { CommonFieldKey, FIELD_LABEL, FieldKey, StandardCommonFieldKey } from '@/proto/field';
import { TimeType } from '@/proto/metric';
import { TdsFormItem, TdsFormItemType, TdsFilterType } from '../form';
import { TdsCustomFieldsValue } from '../tds-custom-fields';
import { TdsColumnGroup } from '../type';
import { customFields2Filter, customFilter, dateCascaderConverter, dateCascaderTransformer } from '../utils';

/**
 * 基础字段池：维护常见标准字段的预设配置
 * 这些字段在多个页面中使用，配置基本相同
 */
export const STANDARD_FIELD_POOL: Record<string, TdsFormItem> = {
  /**
   * 业务下钻 (AbTestTag)
   * 常见配置：使用 customFields2Filter 转换，字段 key 为 'a12'
   */
  [StandardCommonFieldKey.AbTestTag]: {
    name: StandardCommonFieldKey.AbTestTag,
    type: TdsFormItemType.CharacterInput,
    label: i18n.t('业务下钻'),
    inFilter: true,
    filterType: TdsFilterType.Custom,
    filterName: CommonFieldKey.ShiplyAbTestTag,
    filterTransform: (_fieldKey, fieldValue) => customFields2Filter(CommonFieldKey.ShiplyAbTestTag, fieldValue, 'a12'),
    fieldProps: {
      valueSelectProps: {
        fieldKey: CommonFieldKey.AbTestTag,
        // fieldScope 需要根据上下文动态注入
      },
      mode: 'tags',
      modeOptions: CHARACTER_MATCH_ARRAY_OPTIONS,
    },
    group: TdsColumnGroup.Custom,
  },

  /**
   * 实验ID (ShiplyTag)
   * 常见配置：使用 customFields2Filter 转换，字段 key 为 'a11'
   */
  [StandardCommonFieldKey.ShiplyTag]: {
    name: StandardCommonFieldKey.ShiplyTag,
    type: TdsFormItemType.CharacterInput,
    label: i18n.t('实验ID'),
    filterName: CommonFieldKey.ShiplyTag,
    inFilter: true,
    filterType: TdsFilterType.Custom,
    filterTransform: (_fieldKey, fieldValue) => customFields2Filter(CommonFieldKey.ShiplyAbTestTag, fieldValue, 'a11'),
    fieldProps: {
      valueSelectProps: {
        fieldKey: CommonFieldKey.ShiplyTag,
      },
      mode: 'tags',
      modeOptions: CHARACTER_MATCH_ARRAY_OPTIONS,
    },
    group: TdsColumnGroup.Custom,
  },

  /**
   * 个例标签 (TagsInFilter)
   * 常见配置：使用 customFields2Filter 转换，字段 key 为 'a13'
   */
  [StandardCommonFieldKey.TagsInFilter]: {
    name: StandardCommonFieldKey.TagsInFilter,
    type: TdsFormItemType.CharacterInput,
    label: i18n.t('个例标签'),
    filterName: CommonFieldKey.TagsInFilter,
    inFilter: true,
    filterType: TdsFilterType.Custom,
    filterTransform: (_fieldKey, fieldValue) => customFields2Filter(CommonFieldKey.TagsInFilter, fieldValue, 'a13'),
    fieldProps: {
      valueSelectProps: {
        fieldKey: CommonFieldKey.TagsInFilter,
      },
      mode: 'tags',
      modeOptions: CHARACTER_MATCH_ARRAY_OPTIONS,
    },
    group: TdsColumnGroup.Tag,
  },

  /**
   * 用户ID (AccountId)
   */
  [StandardCommonFieldKey.AccountId]: {
    name: StandardCommonFieldKey.AccountId,
    type: TdsFormItemType.CharacterInput,
    label: i18n.t('用户ID'),
    filterName: CommonFieldKey.AccountId,
    inFilter: true,
    filterType: TdsFilterType.String,
    fieldProps: {
      mode: 'tags',
      modeOptions: CHARACTER_MATCH_OPTIONS,
      valueSelectProps: {
        fieldKey: CommonFieldKey.AccountId,
      },
    },
    group: TdsColumnGroup.UserInfo,
  },

  /**
   * 设备ID (Qimei)
   */
  [StandardCommonFieldKey.Qimei]: {
    name: StandardCommonFieldKey.Qimei,
    type: TdsFormItemType.CharacterInput,
    label: i18n.t('设备ID'),
    filterName: CommonFieldKey.Qimei,
    inFilter: true,
    filterType: TdsFilterType.String,
    fieldProps: {
      mode: 'tags',
      modeOptions: CHARACTER_MATCH_OPTIONS,
      valueSelectProps: {
        fieldKey: CommonFieldKey.Qimei,
      },
    },
    group: TdsColumnGroup.DeviceInfo,
  },

  /**
   * Bundle ID
   */
  [StandardCommonFieldKey.BundleId]: {
    name: StandardCommonFieldKey.BundleId,
    type: TdsFormItemType.CharacterInput,
    label: 'Bundle ID',
    filterName: CommonFieldKey.BundleId,
    inFilter: true,
    filterType: TdsFilterType.String,
    fieldProps: {
      valueSelectProps: {
        fieldKey: CommonFieldKey.BundleId,
      },
      mode: 'tags',
      modeOptions: CHARACTER_MATCH_OPTIONS,
    },
    group: TdsColumnGroup.ApplicationInfo,
  },

  /**
   * APP版本
   * 需要注入：fieldScope (Zone), labelTools
   */
  [StandardCommonFieldKey.AppVersion]: {
    name: StandardCommonFieldKey.AppVersion,
    type: TdsFormItemType.CharacterInput,
    label: FIELD_LABEL[FieldKey.AppVersion],
    labelTools: [iconText(versionFilterTips)],
    deleteable: false,
    inFilter: true,
    filterType: TdsFilterType.Version,
    filterName: CommonFieldKey.AppVersion,
    fieldProps: {
      mode: 'tags',
      valueSelectProps: {
        fieldKey: CommonFieldKey.AppVersion,
        // fieldScope 需要根据上下文动态注入
      },
      modeOptions: VERSION_MATCH_OPTIONS,
    },
    group: TdsColumnGroup.ApplicationInfo,
  },

  /**
   * 版本类型 (AppVersionMode)
   * 需要注入：fieldScope (Zone)
   */
  [StandardCommonFieldKey.AppVersionMode]: {
    name: StandardCommonFieldKey.AppVersionMode,
    type: TdsFormItemType.FieldSelector,
    label: i18n.t('版本类型'),
    labelTools: [iconText(enumFilterTips)],
    inFilter: true,
    filterType: TdsFilterType.Custom,
    filterTransform: (_fieldKey, fieldValue) => customFilter(CommonFieldKey.AppVersionMode, { values: fieldValue }),
    fieldProps: {
      mode: 'multiple',
      fieldKey: CommonFieldKey.AppVersionMode,
      // fieldScope 需要根据上下文动态注入
    },
    group: TdsColumnGroup.ApplicationInfo,
  },

  /**
   * SDK版本
   * 需要注入：fieldScope (Zone)
   */
  [StandardCommonFieldKey.SdkVersion]: {
    name: StandardCommonFieldKey.SdkVersion,
    type: TdsFormItemType.CharacterInput,
    label: i18n.t('SDK版本'),
    filterName: CommonFieldKey.SdkVersion,
    inFilter: true,
    filterType: TdsFilterType.Version,
    fieldProps: {
      valueSelectProps: {
        fieldKey: CommonFieldKey.SdkVersion,
        // fieldScope 需要根据上下文动态注入
      },
      mode: 'tags',
      modeOptions: VERSION_MATCH_OPTIONS,
    },
    group: TdsColumnGroup.ApplicationInfo,
  },

  /**
   * 机型 (Model)
   * 需要注入：fieldScope (Zone)
   */
  [StandardCommonFieldKey.Model]: {
    name: StandardCommonFieldKey.Model,
    type: TdsFormItemType.CharacterInput,
    label: i18n.t('机型'),
    filterName: CommonFieldKey.Model,
    inFilter: true,
    filterType: TdsFilterType.String,
    fieldProps: {
      mode: 'tags',
      valueSelectProps: {
        fieldKey: CommonFieldKey.Model,
        // fieldScope 需要根据上下文动态注入
      },
      modeOptions: CHARACTER_MATCH_OPTIONS,
    },
    group: TdsColumnGroup.DeviceInfo,
  },

  /**
   * 系统版本 (OsVersion)
   * 需要注入：fieldScope (Zone)
   */
  [StandardCommonFieldKey.OsVersion]: {
    name: StandardCommonFieldKey.OsVersion,
    type: TdsFormItemType.CharacterInput,
    label: i18n.t('系统版本'),
    filterType: TdsFilterType.Version,
    filterName: CommonFieldKey.OsVersion,
    fieldProps: {
      valueSelectProps: {
        fieldKey: CommonFieldKey.OsVersion,
        // fieldScope 需要根据上下文动态注入
      },
      mode: 'tags',
      modeOptions: VERSION_MATCH_OPTIONS,
    },
    inFilter: true,
    group: TdsColumnGroup.DeviceInfo,
  },

  /**
   * 进程名 (ProcessName)
   * 需要注入：fieldScope (Zone)
   */
  [StandardCommonFieldKey.ProcessName]: {
    name: StandardCommonFieldKey.ProcessName,
    type: TdsFormItemType.CharacterInput,
    label: i18n.t('进程'),
    filterName: CommonFieldKey.ProcessName,
    inFilter: true,
    filterType: TdsFilterType.String,
    fieldProps: {
      mode: 'tags',
      valueSelectProps: {
        fieldKey: CommonFieldKey.ProcessName,
        // fieldScope 需要根据上下文动态注入
      },
      modeOptions: CHARACTER_MATCH_OPTIONS,
    },
    group: TdsColumnGroup.exceptionInfo,
  },

  /**
   * 监控线程 (MonitoredThreadName)
   * 需要注入：fieldScope (Zone)
   */
  [StandardCommonFieldKey.MonitoredThreadName]: {
    name: StandardCommonFieldKey.MonitoredThreadName,
    type: TdsFormItemType.CharacterInput,
    label: i18n.t('监控线程'),
    filterName: CommonFieldKey.MonitoredThreadName,
    inFilter: true,
    filterType: TdsFilterType.String,
    fieldProps: {
      valueSelectProps: {
        fieldKey: CommonFieldKey.MonitoredThreadName,
        // fieldScope 需要根据上下文动态注入
      },
      mode: 'tags',
      modeOptions: CHARACTER_MATCH_OPTIONS,
    },
    defaultEditHidden: true,
    group: TdsColumnGroup.exceptionInfo,
  },

  /**
   * 系统堆栈 (SystemStack)
   * 需要注入：fieldScope (Zone)
   */
  [StandardCommonFieldKey.SystemStack]: {
    name: StandardCommonFieldKey.SystemStack,
    type: TdsFormItemType.FieldSelector,
    label: i18n.t('系统堆栈'),
    filterName: CommonFieldKey.SystemStack,
    inFilter: true,
    filterType: TdsFilterType.EnumString,
    fieldProps: {
      fieldKey: CommonFieldKey.SystemStack,
      // fieldScope 需要根据上下文动态注入
      mode: 'multiple',
    },
    defaultEditHidden: true,
    group: TdsColumnGroup.exceptionInfo,
  },

  /**
   * 时间范围 (TimeRange)
   * 建议注入：fieldProps (maxLimitedDays, timeWindowOptions, timeTypeOptions)，若不注入，则使用默认配置
   */
  [StandardCommonFieldKey.TimeRange]: {
    type: TdsFormItemType.DateCascader,
    label: i18n.t('时间范围'),
    name: StandardCommonFieldKey.TimeRange,
    deleteable: false,
    convertValue: dateCascaderConverter,
    transform: dateCascaderTransformer,
    width: 462,
    inFilter: true,
    filterType: TdsFilterType.DateCascader,
    // fieldProps 需要根据上下文动态注入
    fieldProps: {
      // 限制时间最大可选择范围
      maxLimitedDays: 90,
      // 时间窗口选项
      timeWindowOptions: [
        { label: i18n.t('5分钟聚合'), value: TimeType.MINUTE5 }, // TimeType.MINUTE5-4
        { label: i18n.t('小时聚合'), value: TimeType.HOUR }, // TimeType.HOUR-2
        { label: i18n.t('按天聚合'), value: TimeType.DAY }, // TimeType.DAY-1
        { label: i18n.t('按周聚合'), value: TimeType.WEEK }, // TimeType.WEEK-5
        { label: i18n.t('按月聚合'), value: TimeType.MONTH }, // TimeType.MONTH-6
      ],
      // 时间类型选项
      timeTypeOptions: [
        {
          label: i18n.t('上报时间'),
          value: 'event_time',
        },
        {
          label: i18n.t('发生时间'),
          value: 'report_time',
        },
      ],
    },
  },

  /**
   * 模型 (LlmModel)
   * 需要注入：fieldScope (Zone)
   */
  [StandardCommonFieldKey.LlmModel]: {
    name: StandardCommonFieldKey.LlmModel,
    type: TdsFormItemType.CharacterInput,
    label: i18n.t('模型'),
    fieldProps: {
      mode: 'tags',
      valueSelectProps: {
        fieldKey: FieldKey.LlmModel,
        // fieldScope 需要根据上下文动态注入
      },
      modeOptions: CHARACTER_MATCH_OPTIONS,
    },
    inFilter: true,
    filterName: FieldKey.LlmModel,
    filterType: TdsFilterType.String,
    batchEditMode: true,
    batchEditTips: stringFilterTips,
    group: TdsColumnGroup.ContextInfo,
  },

  /**
   * 画质类型 (GraphicLevelMark)
   * 需要注入：fieldScope (Zone)
   */
  [StandardCommonFieldKey.GraphicLevelMark]: {
    name: StandardCommonFieldKey.GraphicLevelMark,
    type: TdsFormItemType.CharacterInput,
    label: i18n.t('画质'),
    fieldProps: {
      mode: 'tags',
      valueSelectProps: {
        fieldKey: CommonFieldKey.GraphicLevelMask,
      },
      modeOptions: CHARACTER_MATCH_OPTIONS,
    },
    inFilter: true,
    filterType: TdsFilterType.String,
    filterName: CommonFieldKey.GraphicLevelMask,
    group: TdsColumnGroup.ContextInfo,
  },

  /**
   * 网络类型 (NetworkType)
   * 需要注入：fieldScope (Zone)
   */
  [StandardCommonFieldKey.NetWorkType]: {
    type: TdsFormItemType.CharacterInput,
    label: i18n.t('网络类型'),
    name: StandardCommonFieldKey.NetWorkType,
    fieldProps: {
      mode: 'tags',
      valueSelectProps: {
        fieldKey: CommonFieldKey.NetworkType,
        // fieldScope 需要根据上下文动态注入
      },
      modeOptions: CHARACTER_MATCH_OPTIONS,
    },
    inFilter: true,
    filterType: TdsFilterType.String,
    batchEditMode: true,
    batchEditTips: stringFilterTips,
    filterName: CommonFieldKey.NetworkType,
    group: TdsColumnGroup.ContextInfo,
  },

  /**
   * Span名 (Span)
   * 需要注入：fieldScope (Zone)
   */
  [StandardCommonFieldKey.Span]: {
    name: StandardCommonFieldKey.Span,
    type: TdsFormItemType.CharacterInput,
    label: i18n.t('Span名'),
    fieldProps: {
      mode: 'tags',
      valueSelectProps: {
        fieldKey: FieldKey.Span,
        // fieldScope 需要根据上下文动态注入
      },
      modeOptions: CHARACTER_MATCH_OPTIONS,
    },
    inFilter: true,
    filterType: TdsFilterType.String,
    filterName: FieldKey.Span,
    batchEditMode: true,
    batchEditTips: stringFilterTips,
    group: TdsColumnGroup.ContextInfo,
  },
  /**
   * 地域 (Area)
   * 需要注入：fieldScope (Zone)
   */
  [StandardCommonFieldKey.Area]: {
    name: StandardCommonFieldKey.Area,
    type: TdsFormItemType.Area,
    label: i18n.t('地域'),
    inFilter: true,
    filterType: TdsFilterType.Custom,
    filterName: FieldKey.Area,
    hideInCompare: true,
    filterTransform: (_fieldKey: string, fieldValue: TdsCustomFieldsValue[]) => fieldValue?.map((item: any) => ({
      name: item.name,
      operator: item.operator,
      values: item.values || [],
    })) ?? [],
    group: TdsColumnGroup.UserInfo,
  },

  [StandardCommonFieldKey.Stage]: {
    type: TdsFormItemType.CharacterInput,
    label: i18n.t('场景'),
    name: StandardCommonFieldKey.Stage,
    inFilter: true,
    filterType: TdsFilterType.String,
    filterName: CommonFieldKey.Stage,
    fieldProps: {
      mode: 'tags',
      valueSelectProps: {
        fieldKey: CommonFieldKey.Stage,
      },
      modeOptions: CHARACTER_MATCH_OPTIONS,
    },
  },
};
